import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo } from '@/mocks/about';
import { applySeo, getAboutPageSeo, getOrganizationSchema, getBreadcrumbSchema } from '@/utils/seo';

export default function AboutPage() {
  const schemaInjected = useRef(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    applySeo(getAboutPageSeo());

    if (!schemaInjected.current) {
      schemaInjected.current = true;
      const schemas = [
        getOrganizationSchema(),
        getBreadcrumbSchema([{ name: '首页', url: 'https://trainology.github.io' }, { name: '关于我们', url: 'https://trainology.github.io/about' }]),
      ];
      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formDataObj = new FormData(form);

    const websiteAlt = (formDataObj.get('website_alt') as string || '').trim();
    if (websiteAlt) {
      setFormStatus('success');
      return;
    }

    setFormStatus('idle');
    setFormError('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u4l9t3pcjqs2fcsm50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus('error');
        setFormError(parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  return (
    <main>
      <Navbar />
      <div className="pt-20 pb-14 md:pb-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[{ name: '首页', url: '/' }, { name: '关于我们' }]} />

          <section className="mt-10">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                {aboutInfo.brandName}
              </h1>
              <p className="mt-3 text-lg text-primary-600 font-medium">
                {aboutInfo.tagline}
              </p>
              <p className="mt-6 text-sm md:text-base text-foreground-600 leading-relaxed">
                {aboutInfo.introduction}
              </p>
            </div>
          </section>

          <section className="mt-16 md:mt-20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {aboutInfo.stats.map((stat, i) => (
                <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6 text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">
                    {stat.value}
                  </div>
                  <div className="mt-1.5 text-xs md:text-sm text-foreground-600">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-16 md:mt-20">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center">
              核心理念
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutInfo.values.map((val, i) => (
                <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
                    <i className={`${val.icon} text-lg`}></i>
                  </div>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">
                    {val.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                    {val.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-16 md:mt-20">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center">
              专业团队
            </h2>
            <p className="mt-3 text-sm text-foreground-600 text-center max-w-xl mx-auto">
              汇聚行为科学、心理学、教育学和动物行为学领域的专家
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {aboutInfo.team.map((member, i) => (
                  <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-5 text-center">
                    <div className="w-20 h-20 mx-auto rounded-full overflow-hidden">
                      <img
                        src={member.avatarUrl}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="mt-3 font-heading text-sm font-semibold text-foreground-900">
                      {member.name}
                    </h3>
                    <p className="mt-0.5 text-xs text-primary-500 font-medium">
                      {member.role}
                    </p>
                    <p className="mt-2 text-xs text-foreground-600 leading-relaxed">
                      {member.bio}
                    </p>
                  </div>
                ))}
            </div>
          </section>

          <section className="mt-16 md:mt-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  联系我们
                </h2>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  有任何问题或建议？我们期待听到您的声音。填写表单或通过以下方式直接联系我们。
                </p>
                <div className="mt-8 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电话</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微信</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">工作时间</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.workHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                    发送消息
                  </h3>

                  {formStatus === 'success' && (
                    <div className="bg-accent-100 text-accent-700 text-sm rounded-md px-4 py-3 mb-4">
                      消息已成功发送！我们会在1-2个工作日内回复您。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="bg-red-50 text-red-700 text-sm rounded-md px-4 py-3 mb-4">
                      {formError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} data-readdy-form="" id="contact-form">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          主题
                        </label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          required
                          className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          消息
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows={5}
                          maxLength={500}
                          onChange={(e) => setCharCount(e.target.value.length)}
                          className="w-full px-3 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                          placeholder="请输入您的消息（最多500字）"
                        ></textarea>
                        <p className="mt-1 text-xs text-foreground-500 text-right">
                          {charCount}/500
                        </p>
                      </div>

                      <div className="honeypot-field">
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>

                      <button
                        type="submit"
                        className="whitespace-nowrap w-full px-6 py-3 text-sm font-medium bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
                      >
                        发送消息
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </main>
  );
}